import { useEffect, useState } from 'react';
import { BeakerIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type PairPredictionResult } from '../../api/genetics';
import { subscribeGeneticsCatalogChanged } from '../../utils/geneticsCatalogEvents';
import { GENDER_ICON_FEMALE, GENDER_ICON_MALE } from '../../utils/genderDisplay';

interface PairPredictionPreviewProps {
  maleBirdId?: string;
  femaleBirdId?: string;
  speciesId?: string;
  compact?: boolean;
}

export default function PairPredictionPreview({
  maleBirdId,
  femaleBirdId,
  speciesId,
  compact = false,
}: PairPredictionPreviewProps) {
  const [prediction, setPrediction] = useState<PairPredictionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canPredict = !!maleBirdId && !!femaleBirdId;

  const loadPrediction = async () => {
    if (!maleBirdId || !femaleBirdId) return;
    setLoading(true);
    setError(null);
    try {
      if (speciesId) {
        await geneticsApi.seedSpecies(speciesId);
      }
      const result = await geneticsApi.predictPair(maleBirdId, femaleBirdId);
      setPrediction(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load genetics prediction');
      setPrediction(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canPredict) {
      void loadPrediction();
    } else {
      setPrediction(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maleBirdId, femaleBirdId, speciesId]);

  useEffect(
    () => subscribeGeneticsCatalogChanged(speciesId, () => void loadPrediction()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [speciesId, maleBirdId, femaleBirdId],
  );

  if (!canPredict) return null;

  return (
    <div className={`rounded-lg border border-indigo-200 bg-indigo-50/50 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
          <BeakerIcon className="h-4 w-4" />
          Expected Offspring Genetics
        </h4>
        <button
          type="button"
          onClick={loadPrediction}
          disabled={loading}
          className="text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
        >
          {loading ? 'Calculating…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

      {prediction?.warnings?.length ? (
        <div className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          <div className="flex items-start gap-1">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>{prediction.warnings[0]}</span>
          </div>
        </div>
      ) : null}

      {prediction?.combinedPhenotypes?.length ? (
        <div className="mb-2">
          <p className="text-xs font-medium text-slate-700 mb-1">Top combined phenotypes</p>
          <div className="space-y-0.5">
            {prediction.combinedPhenotypes.slice(0, compact ? 3 : 5).map((item) => (
              <div key={item.phenotype} className="text-xs flex justify-between gap-2">
                <span className="text-slate-800">{item.phenotype}</span>
                <span className="font-medium text-indigo-700">{(item.probability * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {prediction?.loci?.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-indigo-700 hover:text-indigo-900">
            Per-locus breakdown ({prediction.loci.length} loci)
          </summary>
          <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
            {prediction.loci
              .filter((locus) => {
                const onlyWild =
                  locus.outcomes.length === 1 &&
                  locus.outcomes[0].probability >= 0.999 &&
                  !locus.outcomes[0].isVisual &&
                  !locus.outcomes[0].isSplit &&
                  locus.outcomes[0].phenotype === 'Wild type';
                const parentsWild =
                  locus.parentMale?.phenotype === 'Wild type' &&
                  locus.parentFemale?.phenotype === 'Wild type';
                return !(onlyWild && parentsWild);
              })
              .map((locus) => (
              <div key={locus.locusSymbol} className="bg-white rounded border border-indigo-100 p-2">
                <div className="font-medium text-slate-800 mb-1">
                  {locus.locusName} <span className="font-mono text-slate-400">({locus.locusSymbol})</span>
                </div>
                {(locus.parentMale || locus.parentFemale) && (
                  <p className="text-[10px] text-slate-400 mb-1">
                    {GENDER_ICON_MALE} {locus.parentMale?.genotype ?? '?'} · {GENDER_ICON_FEMALE} {locus.parentFemale?.genotype ?? '?'}
                  </p>
                )}
                {locus.outcomes.slice(0, 4).map((o) => (
                  <div key={`${o.genotype}-${o.sex}`} className="flex justify-between gap-2 py-0.5">
                    <span className="text-slate-600 truncate">
                      {o.phenotype}
                      {o.sex !== 'EITHER' && (o.sex === 'MALE' ? ` ${GENDER_ICON_MALE}` : ` ${GENDER_ICON_FEMALE}`)}
                    </span>
                    <span className="text-indigo-700 flex-shrink-0">{(o.probability * 100).toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      ) : !loading && !error ? (
        <p className="text-xs text-slate-600">
          Record structured genotypes on parent birds for accurate predictions.
        </p>
      ) : null}
    </div>
  );
}
